/**
 * AWS API Gateway transport.
 *
 * The gateway fronts the Go services (hotels, …) and validates the same
 * access token the mango-api transport uses, so this reuses the app session
 * JWTs straight from the auth state — no separate login.
 *
 * Kept separate from the mango-api client only because the base URL differs;
 * the bearer header, 401 refresh-and-retry, and FormData handling all come
 * from the shared interceptors.
 *
 * Refresh is delegated to `getApiInst().getRefreshToken()`, which coalesces
 * concurrent calls behind one in-flight promise. That matters because refresh
 * tokens are one-time and rotate: two transports refreshing independently
 * would race, one would consume the token, and the other's refresh would fail.
 * Delegating rather than re-implementing keeps both on a single POST.
 *
 * `onAuthFailure` is wired for the same reason — see below.
 */
import { getCurrentAuthState } from '~/atoms/AuthenticationState'
import { getApiInst, onAuthenticationResult } from '~/atoms/MangoApi'
import { domains } from '~/constants/domains'
import { AuthenticationType } from '~/types/MangoApiClientTypes'
import { getAppVersion, isElectron } from '~/utils/common'

import { createAuthenticatedClient } from '@mangovoice/api-client'
import type {
  ApiClientAuthData,
  ApiError,
  AuthFailureInfo,
  HttpTransport,
} from '@mangovoice/api-client'

const getAuthData = (): ApiClientAuthData => {
  const currentState = getCurrentAuthState()
  return {
    validationToken: currentState.validationToken?.token ?? null,
    accessToken: currentState.appSessionJwts?.access_token ?? null,
    refreshToken: currentState.appSessionJwts?.refresh_token ?? null,
    sessionToken: currentState.session?.token.key ?? null,
  }
}

const create = (): HttpTransport => {
  const { instance, transport } = createAuthenticatedClient({
    baseURL: domains.GATEWAY(),
    appName: isElectron ? 'desktop' : 'web',
    appVersion: '0.0.0',
    timeout: 10_000,
    auth: {
      getAuthData,
      refreshToken: () => getApiInst().getRefreshToken(),
      // Without this the interceptor would reject the request and leave the
      // session untouched, so an expired session would log the user out or
      // not depending on which transport happened to 401 first. Routing
      // through the same callback the mango-api client uses keeps both
      // transports agreeing on session state.
      onAuthFailure: (info: AuthFailureInfo) => {
        onAuthenticationResult({
          authenticationType:
            info.type === 'invalid_authentication'
              ? AuthenticationType.InvalidAuthentication
              : AuthenticationType.RefreshAppSessionJwts,
          ok: false,
          result: info.error as ApiError,
        })
      },
    },
  })

  getAppVersion().then((version) => {
    instance.setHeader('x-mango-app-version', version)
  })

  return transport
}

let gatewayTransport: HttpTransport | undefined

export const getGatewayTransport = (): HttpTransport => {
  gatewayTransport ??= create()
  return gatewayTransport
}
